import '../../styles/Home.css';
import activityData from '../../data/steamActivity.json';
import { useParams } from 'react-router-dom';
import NotFoundPage from '../NotFoundPage';

const activityKeysByRoute = {
  math: 'mathTest',
  art: 'artWorkshop',
  'essay-writing': 'essayWriting',
  'print-workshop': 'printWorkshop',
  'science-test': 'scienceTest',
  'science-fair': 'scienceFair',
  'spelling-bee': 'spellingBee',
  debate: 'debate',
};

const RichText = ({ content }) => {
  if (typeof content === 'string') {
    return content;
  }

  return content.map((part, index) => {
    if (typeof part === 'string') {
      return part;
    }

    const text = part.href
      ? <a href={part.href}>{part.text}</a>
      : part.text;

    return part.bold
      ? <b key={index}>{text}</b>
      : <span key={index}>{text}</span>;
  });
};

const ActivityList = ({ items }) => (
  <ul>
    {items.map((item, index) => {
      const content = item.content || item;

      return (
        <li key={index}>
          <RichText content={content} />
          {item.children && <ActivityList items={item.children} />}
        </li>
      );
    })}
  </ul>
);

const ActivityPage = ({ activityKey }) => {
  const { id } = useParams();
  const selectedActivityKey = activityKey || activityKeysByRoute[id];
  const activity = selectedActivityKey && activityData[selectedActivityKey];

  if (!activity) {
    return <NotFoundPage />;
  }

  return (
    <div className="container">
      <div className="row">
        {activity.logos.map((logo) => (
          <div className="col-sm" key={logo.src}>
            <p className="center-align">
              <img src={logo.src} alt={logo.alt} width="40%" className="rounded" />
            </p>
          </div>
        ))}
      </div>

      <div>
        <h4 className="center-align"><b><u>{activity.title}</u></b></h4>
        {activity.notice && <h4 className="center-align cFF5833">{activity.notice}</h4>}
      </div>
      <p>&nbsp;</p>
      <div>
        <h5 className="justify c737373"><b><RichText content={activity.introduction.heading} /></b></h5>
        {activity.introduction.paragraphs.map((paragraph, index) => (
          <h5 className="justify c737373" key={index}><RichText content={paragraph} /></h5>
        ))}
      </div>

      <p>&nbsp;</p>
      <div><hr className="hr-line" /></div>

      {activity.details && (
        <>
          <div className="row">
            <div className="col-sm">
              <h4 className="center-align"><b>{activity.details.heading}</b></h4>
              <h5 className="justify c737373"><i><b>Date:</b> {activity.details.date}</i></h5>
              <h5 className="justify c737373"><i><b>Duration:</b> {activity.details.duration}</i></h5>
              <h5 className="justify c737373"><i><b>{activity.details.levelsHeading}</b></i></h5>
            </div>
          </div>
          <div className="row">
            <div className="col-sm">
              <ul>
                {activity.details.levels.map((level) => (
                  <li key={level.name}>
                    <b>{level.name}:</b> {level.grades} | {level.time}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div><hr className="hr-line" /></div>
        </>
      )}

      {activity.sections.map((section) => (
        <div key={section.heading}>
          <div className="row">
            <div className="col-sm">
              <h4 className="center-align"><b>{section.heading}</b></h4>
              {section.paragraphs && section.paragraphs.map((paragraph, index) => (
                <h5
                  className={`${section.centered ? 'center-align' : 'justify'} c737373`}
                  key={index}
                >
                  <i><RichText content={paragraph} /></i>
                </h5>
              ))}
              {section.items && <ActivityList items={section.items} />}
              {section.conclusion && (
                <h5 className="justify c737373"><RichText content={section.conclusion} /></h5>
              )}
            </div>
          </div>
          <div><hr className="hr-line" /></div>
        </div>
      ))}

      {activity.resource && (
        <div className="row">
          <div className="col-sm" />
          <div className="col-sm">
            <p className="center-align">
              <a href={activity.resource.url}>
                <img
                  src={activity.resource.image}
                  alt={activity.resource.alt}
                  width="100"
                  height="100"
                  className="rounded"
                />
              </a>
            </p>
            <h5 className="center-align">
              <a href={activity.resource.url}>{activity.resource.label}</a>
            </h5>
          </div>
          <div className="col-sm" />
        </div>
      )}

      {activity.callToAction && (
        <>
          <p>&nbsp;</p>
          <div className="row">
            <div className="col-sm">
              <h4 className="center-align"><b>{activity.callToAction.heading}</b></h4>
              {activity.callToAction.description && (
                <h5 className="center-align"><i><RichText content={activity.callToAction.description} /></i></h5>
              )}
              {activity.flyer && !activity.flyer.afterCallToAction && (
                <>
                  {activity.flyer.heading && (
                    <h4 className="center-align"><b>{activity.flyer.heading}</b></h4>
                  )}
                  <h5 className="center-align">
                    <i>
                      <RichText content={activity.flyer.introduction} />
                      {' '}
                      <a href={activity.flyer.url}>{activity.flyer.linkText}</a>
                      {' '}
                      <RichText content={activity.flyer.conclusion} />
                    </i>
                  </h5>
                </>
              )}
              {activity.callToAction.buttonText && (
                <>
                  {activity.callToAction.buttonIntroduction && (
                    <h5 className="center-align">
                      <i><RichText content={activity.callToAction.buttonIntroduction} /></i>
                    </h5>
                  )}
                  <h5 className="center-align">
                    <a
                      type="button"
                      className="btn btn-primary btn-lg"
                      href={activity.callToAction.url}
                    >
                      {activity.callToAction.buttonText}
                    </a>
                  </h5>
                </>
              )}
              {activity.callToAction.conclusion && (
                <h5 className="center-align">
                  <i><RichText content={activity.callToAction.conclusion} /></i>
                </h5>
              )}
            </div>
          </div>
        </>
      )}

      {activity.flyer && (!activity.callToAction || activity.flyer.afterCallToAction) && (
        <div className="row">
          <div className="col-sm">
            {activity.flyer.heading && (
              <h4 className="center-align"><b>{activity.flyer.heading}</b></h4>
            )}
            <h5 className="center-align">
              <i>
                <RichText content={activity.flyer.introduction} />
                {' '}
                <a href={activity.flyer.url}>{activity.flyer.linkText}</a>
                {' '}
                <RichText content={activity.flyer.conclusion} />
              </i>
            </h5>
          </div>
        </div>
      )}

      <p>&nbsp;</p>
      <div><hr className="hr-line" /></div>
    </div>
  );
};

export default ActivityPage;
